import { ApiError } from '../api/client';
import type { DeliveryNote, SignOnScreenInput } from '../delivery-notes/api';
import { mapDeliveryNoteError } from '../delivery-notes/delivery-note-api-errors';
import type { OfflineStore } from '../offline/offline-store';

/**
 * A signature captured on the phone. `clientCaptureId` is stable per capture
 * (replays return the original result) and `clientSignedAt` is the device time
 * at which the person signed, not the time it reaches the server.
 */
export interface SignatureCapture extends SignOnScreenInput {
  clientCaptureId: string;
  clientSignedAt: string;
}

export type QueueProblemCode = 'VOIDED' | 'ALREADY_SIGNED' | 'REJECTED';

export interface QueuedSignature {
  /** `userId:noteId`: one open capture per note and user. */
  key: string;
  userId: string;
  noteId: string;
  capture: SignatureCapture;
  queuedAt: string;
  /** `pending` keeps retrying; `problem` waits for the user to dismiss it. */
  state: 'pending' | 'problem';
  problem?: { code: QueueProblemCode; message: string };
}

export type AttemptResult =
  | { kind: 'synced'; note: DeliveryNote }
  | { kind: 'problem'; item: QueuedSignature; error: unknown }
  | { kind: 'auth' }
  | { kind: 'retry' };

export interface SyncSummary {
  synced: DeliveryNote[];
  authRequired: boolean;
}

/** A second capture of a note that still has one waiting (or a problem the user has not dismissed). */
export class PendingCaptureError extends Error {
  constructor() {
    super('This delivery note already has a signature waiting to sync');
    this.name = 'PendingCaptureError';
  }
}

const DEFAULT_TIMEOUT_MS = 20_000;

function apiCode(error: ApiError): string | undefined {
  return (error.body as { code?: string } | null)?.code;
}

function problemCodeFor(error: ApiError): QueueProblemCode {
  const code = apiCode(error);

  if (error.status === 409 && code === 'DELIVERY_NOTE_VOIDED') {
    return 'VOIDED';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_ALREADY_SIGNED') {
    return 'ALREADY_SIGNED';
  }

  return 'REJECTED';
}

/** The server answered and said no: retrying the same request cannot change that (401 renews the session, 408/429 are transient). */
export function isDefiniteRejection(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    ![401, 408, 429].includes(error.status)
  );
}

/** Runs `run` with a signal that aborts after `ms`; a `run` that ignores the signal still stops being waited for. */
export async function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error('Request timed out'));
    }, ms);
  });

  try {
    return await Promise.race([run(controller.signal), timedOut]);
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  } finally {
    clearTimeout(timer);
  }
}

/** The slice of `navigator.locks` the queue uses, so tests can fake another tab. */
export interface SyncLocks {
  request: <R>(
    name: string,
    options: { ifAvailable: true },
    callback: (lock: object | null) => Promise<R>,
  ) => Promise<R>;
}

function browserLocks(): SyncLocks | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.locks;
}

export interface SignatureQueueDeps {
  store: OfflineStore;
  send: (noteId: string, capture: SignatureCapture, signal: AbortSignal) => Promise<DeliveryNote>;
  timeoutMs?: number;
  /** Cross-tab single flight; defaults to `navigator.locks` when the browser has it. */
  locks?: SyncLocks;
}

/**
 * The durable outbox of signatures. A capture is written here before any
 * network call and only removed once the server answered 200 (a replay of the
 * same capture id also answers 200) or the user dismissed a problem. Every
 * change to a stored item is one atomic store update that checks the item is
 * still the one the caller saw, so a slow request can neither resurrect what a
 * tab dismissed nor remove a newer capture.
 */
export function createSignatureQueue({
  store,
  send,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  locks = browserLocks(),
}: SignatureQueueDeps) {
  const keyOf = (userId: string, noteId: string): string => `${userId}:${noteId}`;
  const inFlight = new Set<string>();

  const list = async (userId: string): Promise<QueuedSignature[]> => {
    const entries = await store.entries<QueuedSignature>('queue');

    return entries
      .map(([, item]) => item)
      .filter((item) => item.userId === userId)
      .sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
  };

  const remove = (userId: string, noteId: string): Promise<void> => store.remove('queue', keyOf(userId, noteId));

  const isSameCapture = (stored: QueuedSignature | undefined, item: QueuedSignature): stored is QueuedSignature =>
    stored?.capture.clientCaptureId === item.capture.clientCaptureId;

  const sendOnce = async (item: QueuedSignature): Promise<AttemptResult> => {
    // A list taken before another tab synced or dismissed this item must not send it again.
    const current = await store.get<QueuedSignature>('queue', item.key);

    if (!isSameCapture(current, item) || current.state !== 'pending') {
      return { kind: 'retry' };
    }

    try {
      const note = await withTimeout((signal) => send(item.noteId, item.capture, signal), timeoutMs);

      await store.update<QueuedSignature>('queue', item.key, (stored) =>
        isSameCapture(stored, item) ? { op: 'remove' } : { op: 'keep' },
      );

      return { kind: 'synced', note };
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return { kind: 'auth' };
      }

      if (!isDefiniteRejection(error)) {
        // Network error, timeout, 5xx: the capture id makes a retry safe, even if the first try arrived.
        return { kind: 'retry' };
      }

      const problem: QueuedSignature = {
        ...item,
        state: 'problem',
        problem: { code: problemCodeFor(error), message: mapDeliveryNoteError(error) },
      };
      const stored = await store.update<QueuedSignature>('queue', item.key, (current) =>
        isSameCapture(current, item) ? { op: 'put', value: problem } : { op: 'keep' },
      );

      // Dismissed, replaced or synced elsewhere meanwhile: there is nothing left to mark.
      return isSameCapture(stored, item) ? { kind: 'problem', item: problem, error } : { kind: 'retry' };
    }
  };

  /** One request per item at a time: in this tab by key, across tabs by a Web Lock when the browser has one. */
  const attempt = async (item: QueuedSignature): Promise<AttemptResult> => {
    if (inFlight.has(item.key)) {
      return { kind: 'retry' };
    }

    inFlight.add(item.key);

    try {
      if (locks === undefined) {
        return await sendOnce(item);
      }

      return await locks.request(`signature-sync:${item.key}`, { ifAvailable: true }, (lock) =>
        lock === null ? Promise.resolve<AttemptResult>({ kind: 'retry' }) : sendOnce(item),
      );
    } finally {
      inFlight.delete(item.key);
    }
  };

  return {
    list,
    remove,
    attempt,
    dismiss: remove,

    enqueue: async (userId: string, noteId: string, capture: SignatureCapture): Promise<QueuedSignature> => {
      const item: QueuedSignature = {
        key: keyOf(userId, noteId),
        userId,
        noteId,
        capture,
        queuedAt: new Date().toISOString(),
        state: 'pending',
      };
      const existing = await store.update<QueuedSignature>('queue', item.key, (current) =>
        current === undefined ? { op: 'put', value: item } : { op: 'keep' },
      );

      if (existing !== undefined) {
        throw new PendingCaptureError();
      }

      return item;
    },

    syncAll: async (userId: string): Promise<SyncSummary> => {
      const summary: SyncSummary = { synced: [], authRequired: false };

      for (const item of await list(userId)) {
        if (item.state !== 'pending') {
          continue;
        }

        const result = await attempt(item);

        if (result.kind === 'synced') {
          summary.synced.push(result.note);
        } else if (result.kind === 'auth') {
          summary.authRequired = true;
          break;
        }
      }

      return summary;
    },
  };
}

export type SignatureQueue = ReturnType<typeof createSignatureQueue>;
